import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { CallStateStore } from './call/call-state.store';
import { CallSession, CallDirection, CallState } from '../../types/telnyx-webrtc';

/**
 * Call context snapshot captured at call initiation.
 * This data is immutable during the call lifecycle and survives navigation.
 */
export interface CallContext {
  /** Unique session identifier */
  sessionId: string;
  /** Lead ID associated with this call */
  leadId: number | null;
  /** Lead display name (captured at call start) */
  leadName: string;
  /** Phone number being called or caller's number */
  phoneNumber: string;
  /** Call direction: inbound or outbound */
  direction: CallDirection;
  /** Timestamp when context was captured */
  capturedAt: Date;
  /** Additional lead metadata */
  leadMetadata?: {
    email?: string;
    campus?: string;
    program?: string;
    owner?: string;
    status?: string;
  };
}

/**
 * Lead information passed when initiating a call.
 * Used to capture context before call starts.
 */
export interface LeadInfo {
  id: number;
  name: string;
  phone?: string;
  email?: string;
  campus?: string;
  program?: string;
  owner?: string;
  status?: string;
}

/**
 * GlobalCallContextService
 *
 * Captures and stores call context (lead information) at call initiation time.
 * This context persists across navigation and is independent of route/component inputs.
 *
 * Key responsibilities:
 * - Capture lead context when outbound call is initiated
 * - Capture caller context when inbound call is answered
 * - Provide immutable context during call lifecycle
 * - Clear context when call ends
 *
 * Usage:
 * - Components read from `currentContext()` signal
 * - Never pass context via @Input bindings for active calls
 */
@Injectable({
  providedIn: 'root'
})
export class GlobalCallContextService {
  private callStore = inject(CallStateStore);

  // ============================================================================
  // State Signals
  // ============================================================================

  /** Current call context (null when no active call) */
  private _currentContext = signal<CallContext | null>(null);

  /** Track the last captured session ID to prevent duplicate captures */
  private _lastCapturedSessionId: string | null = null;

  // ============================================================================
  // Public Signals (readonly)
  // ============================================================================

  /** Current call context for UI consumption */
  readonly currentContext = this._currentContext.asReadonly();

  /** Whether there is an active context */
  readonly hasContext = computed(() => this._currentContext() !== null);

  /** Lead display name from context */
  readonly leadDisplayName = computed(() => {
    const ctx = this._currentContext();
    return ctx?.leadName || 'Unknown Contact';
  });

  /** Phone number from context */
  readonly phoneNumber = computed(() => {
    const ctx = this._currentContext();
    return ctx?.phoneNumber || '';
  });

  /** Call direction from context */
  readonly direction = computed(() => {
    const ctx = this._currentContext();
    return ctx?.direction || null;
  });

  /** Lead ID from context */
  readonly leadId = computed(() => {
    const ctx = this._currentContext();
    return ctx?.leadId || null;
  });

  /** Lead initials for avatar display */
  readonly leadInitials = computed(() => {
    const name = this.leadDisplayName();
    if (!name || name === 'Unknown Contact') return '?';

    const parts = name.trim().split(' ').filter(p => p.length > 0);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return parts[0]?.[0]?.toUpperCase() || '?';
  });

  constructor() {
    // Auto-clear context when call ends (transitions to idle)
    effect(() => {
      const state = this.callStore.callState();
      const activeCall = this.callStore.activeCall();

      // Clear context when call transitions to idle and no active call
      if (state === 'idle' && !activeCall && this._currentContext()) {
        // Delay clear to allow disposition modal to use context
        setTimeout(() => {
          // Double-check state hasn't changed
          if (this.callStore.callState() === 'idle' && !this.callStore.activeCall()) {
            this.clearContext();
          }
        }, 500); // 5 second delay for disposition flow
      }
    }, { allowSignalWrites: true });
  }

  // ============================================================================
  // Public Methods
  // ============================================================================

  /**
   * Capture call context for an outbound call.
   * Called when initiating a call with lead information.
   *
   * @param session - The call session from CallFacadeService
   * @param leadInfo - Lead information to capture
   */
  captureOutboundContext(session: CallSession, leadInfo?: LeadInfo): void {
    // Prevent duplicate capture for same session
    if (this._lastCapturedSessionId === session.id) {
      console.log('📞 [GlobalCallContext] Context already captured for session:', session.id);
      return;
    }

    const context: CallContext = {
      sessionId: session.id,
      leadId: leadInfo?.id || session.leadId || null,
      leadName: leadInfo?.name || this.extractLeadName(session) || 'Unknown Contact',
      phoneNumber: session.phoneNumber,
      direction: 'outbound',
      capturedAt: new Date(),
      leadMetadata: leadInfo ? {
        email: leadInfo.email,
        campus: leadInfo.campus,
        program: leadInfo.program,
        owner: leadInfo.owner,
        status: leadInfo.status,
      } : undefined,
    };

    this._currentContext.set(context);
    this._lastCapturedSessionId = session.id;

    console.log('📞 [GlobalCallContext] Outbound context captured:', {
      sessionId: context.sessionId,
      leadId: context.leadId,
      leadName: context.leadName,
      phoneNumber: context.phoneNumber,
    });
  }

  /**
   * Capture call context for an inbound call.
   * Called when answering an incoming call.
   *
   * @param session - The call session from CallFacadeService
   * @param callerInfo - Caller information (from SIP headers or lead lookup)
   */
  captureInboundContext(session: CallSession, callerInfo?: {
    id?: number;
    name?: string;
    phone?: string;
    campus?: string;
    program?: string;
    owner?: string;
  }): void {
    // Prevent duplicate capture for same session
    if (this._lastCapturedSessionId === session.id) {
      console.log('📞 [GlobalCallContext] Context already captured for session:', session.id);
      return;
    }

    const context: CallContext = {
      sessionId: session.id,
      leadId: callerInfo?.id || session.leadId || null,
      leadName: callerInfo?.name || this.extractLeadName(session) || 'Unknown Caller',
      phoneNumber: callerInfo?.phone || session.phoneNumber,
      direction: 'inbound',
      capturedAt: new Date(),
      leadMetadata: callerInfo ? {
        campus: callerInfo.campus,
        program: callerInfo.program,
        owner: callerInfo.owner,
      } : undefined,
    };

    this._currentContext.set(context);
    this._lastCapturedSessionId = session.id;

    console.log('📞 [GlobalCallContext] Inbound context captured:', {
      sessionId: context.sessionId,
      leadId: context.leadId,
      leadName: context.leadName,
      phoneNumber: context.phoneNumber,
    });
  }

  /**
   * Update context with additional lead information.
   * Used when lead info arrives late (e.g., from async lookup).
   *
   * @param updates - Partial context updates
   */
  updateContext(updates: Partial<Pick<CallContext, 'leadId' | 'leadName' | 'leadMetadata'>>): void {
    const current = this._currentContext();
    if (!current) {
      console.warn('📞 [GlobalCallContext] Cannot update: no active context');
      return;
    }

    this._currentContext.set({
      ...current,
      ...updates,
      leadMetadata: {
        ...current.leadMetadata,
        ...updates.leadMetadata,
      },
    });

    console.log('📞 [GlobalCallContext] Context updated:', updates);
  }

  /**
   * Clear the current call context.
   * Called when call ends and disposition is complete.
   */
  clearContext(): void {
    const current = this._currentContext();
    if (current) {
      console.log('📞 [GlobalCallContext] Context cleared for session:', current.sessionId);
    }

    this._currentContext.set(null);
    this._lastCapturedSessionId = null;
  }

  /**
   * Check if context matches a specific session.
   *
   * @param sessionId - Session ID to check
   * @returns True if context is for this session
   */
  isContextForSession(sessionId: string): boolean {
    return this._currentContext()?.sessionId === sessionId;
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  /**
   * Extract lead name from session if available.
   *
   * @param session - Call session
   * @returns Lead name or null
   */
  private extractLeadName(session: CallSession): string | null {
    // Check if session has lead name attached (from SIP headers or prior lookup)
    const sessionAny = session as any;
    if (sessionAny.leadName) {
      return sessionAny.leadName;
    }
    if (sessionAny.callerName) {
      return sessionAny.callerName;
    }
    return null;
  }
}
